import EnvVariablesLoader from './EnvVariablesLoader.js'

import defaultLogoUrl from './assets/images/default/logo.png'
import defaultLogoMobileUrl from './assets/images/default/logo-mobile.png'
import defaultCreditsUrl from './assets/images/default/credits.png'

const BUCKET_URL = EnvVariablesLoader.getVar('BUCKET_URL')
const API_BASE_URL = EnvVariablesLoader.getVar('API_BASE_URL')
const DEFAULT_LOGO_IMAGE_NAME = 'logo.png'
const DEFAULT_LOGO_MOBILE_IMAGE_NAME = 'logo-mobile.png'
const DEFAULT_CREDITS_IMAGE_NAME = 'credits.png'
const DEFAULT_TENANT_NAME = 'default'
const DEFAULT_WEBSITE_URL = 'https://www.haltadefinizione.com/'

const tenantsUsingCreditsByDefault = new Set([
  'brera',
  'ff2020',
  'genericExternal',
  'sanRaffaele',
  'pololombardia',
  'gnaabarberini',
  'fabrizioferrari',
  'fondazionevignola',
  'pierluigisterpi',
  'archiviostatomodena',
  'estense',
  'galleriaaccademiafirenze',
  'galleriaborghese',
  'bargellomusei',
  'Uffizi',
  'polotoscana',
  'ballester',
  'scrovegni',
  'arcidiocesitorino',
  'santamariadellascala',
  'open'
])

const getTenantUrl = tenant => `${BUCKET_URL}/image/${tenant}`

let detectTenantImageErrorsEventInjected = false
const tenantImageFallbackMap = new Map()
const tenantInfoRequestCache = new Map()

function normalizeImageUrl(imageUrl) {
  if (typeof imageUrl !== 'string') return ''
  return imageUrl.split('#')[0].split('?')[0]
}

function registerTenantImageFallback(imageUrl, fallbackSrc) {
  tenantImageFallbackMap.set(normalizeImageUrl(imageUrl), fallbackSrc)
  return imageUrl
}

function resolveImageName(imageName, defaultImageName) {
  if (typeof imageName === 'string' && imageName.trim()) {
    return imageName.trim()
  }

  return defaultImageName
}

function resolveString(value, fallback = '') {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback
}

function normalizeViewersConfig(viewersConfig) {
  if (!viewersConfig || typeof viewersConfig !== 'object') return {}

  const normalizedConfig = {}
  const analyticsCustomerCode = resolveString(viewersConfig.analyticsCustomerCode)
  if (analyticsCustomerCode) {
    normalizedConfig.analyticsCustomerCode = analyticsCustomerCode
  }

  ;[
    'sendInternalAnalytics',
    'showCreditsInsteadOfSimpleLogo',
    'showLogoInFullScreen',
    'hideToolbar',
    'hideLogoAndCredits'
  ].forEach(propertyName => {
    if (typeof viewersConfig[propertyName] === 'boolean') {
      normalizedConfig[propertyName] = viewersConfig[propertyName]
    }
  })

  return normalizedConfig
}

function joinUrl(baseUrl, path) {
  const normalizedBaseUrl = resolveString(baseUrl).replace(/\/+$/, '')
  const normalizedPath = resolveString(path).replace(/^\/+/, '')
  return normalizedBaseUrl ? `${normalizedBaseUrl}/${normalizedPath}` : `/${normalizedPath}`
}

function escapeForRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function detectTenantImageErrors() {
  if (detectTenantImageErrorsEventInjected) return
  detectTenantImageErrorsEventInjected = true

  if (typeof document === 'undefined' || !document.body) {
    console.warn('Assets Service: Cannot inject image error handler, document.body not available.')
    return
  }

  document.body.addEventListener(
    'error',
    event => {
      if (!event.target || event.target.tagName !== 'IMG') return

      const imgSrc = event.target.src

      if (imgSrc.startsWith('data:')) return

      const registeredFallbackSrc = tenantImageFallbackMap.get(normalizeImageUrl(imgSrc))
      if (registeredFallbackSrc) {
        console.error(
          `Assets Service - Failed to load image: ${imgSrc}`,
          'it will be replaced with the bundled default one'
        )
        event.target.src = registeredFallbackSrc
        return
      }

      if (BUCKET_URL) {
        const escapedBucketUrl = escapeForRegExp(BUCKET_URL)
        const logoPattern = new RegExp(`${escapedBucketUrl}/image/.*/${escapeForRegExp(DEFAULT_LOGO_IMAGE_NAME)}`)
        const logoMobilePattern = new RegExp(
          `${escapedBucketUrl}/image/.*/${escapeForRegExp(DEFAULT_LOGO_MOBILE_IMAGE_NAME)}`
        )
        const creditsPattern = new RegExp(`${escapedBucketUrl}/image/.*/${escapeForRegExp(DEFAULT_CREDITS_IMAGE_NAME)}`)

        let fallbackSrc = null

        if (logoPattern.test(imgSrc)) {
          fallbackSrc = defaultLogoUrl
        } else if (logoMobilePattern.test(imgSrc)) {
          fallbackSrc = defaultLogoMobileUrl
        } else if (creditsPattern.test(imgSrc)) {
          fallbackSrc = defaultCreditsUrl
        }

        if (fallbackSrc) {
          console.error(
            `Assets Service - Failed to load image: ${imgSrc}`,
            'it will be replaced with the bundled default one'
          )
          event.target.src = fallbackSrc
        }
      } else {
        console.warn('Assets Service: BUCKET_URL not defined, cannot apply image fallback logic based on URL patterns.')
      }
    },
    true
  )
}

function injectEventIfNeeded() {
  if (typeof window !== 'undefined') {
    detectTenantImageErrors()
  }
}

export const getLogoImage = (tenant, imageName = DEFAULT_LOGO_IMAGE_NAME) => {
  injectEventIfNeeded()
  const logoImageName = resolveImageName(imageName, DEFAULT_LOGO_IMAGE_NAME)
  const logoImageUrl = `${getTenantUrl(tenant)}/${logoImageName}`
  return registerTenantImageFallback(logoImageUrl, defaultLogoUrl)
}

export const getLogoMobile = (tenant, imageName = DEFAULT_LOGO_MOBILE_IMAGE_NAME) => {
  injectEventIfNeeded()
  const logoMobileImageName = resolveImageName(imageName, DEFAULT_LOGO_MOBILE_IMAGE_NAME)
  const logoMobileImageUrl = `${getTenantUrl(tenant)}/${logoMobileImageName}`
  return registerTenantImageFallback(logoMobileImageUrl, defaultLogoMobileUrl)
}

export const getCreditsImage = tenant => {
  injectEventIfNeeded()
  const creditsImageUrl = `${getTenantUrl(tenant)}/${DEFAULT_CREDITS_IMAGE_NAME}`
  return registerTenantImageFallback(creditsImageUrl, defaultCreditsUrl)
}

function resolveBackendImage(imageUrl, fallbackSrc, tenantImageUrl) {
  injectEventIfNeeded()
  const resolvedImageUrl = resolveString(imageUrl, tenantImageUrl)
  return registerTenantImageFallback(resolvedImageUrl, fallbackSrc)
}

function getDefaultAsset(tenantName, viewersConfig) {
  if (typeof viewersConfig.showCreditsInsteadOfSimpleLogo === 'boolean') {
    return viewersConfig.showCreditsInsteadOfSimpleLogo ? 'credits' : 'logo'
  }

  return tenantsUsingCreditsByDefault.has(tenantName) ? 'credits' : 'logo'
}

function getTenantInfoEndpoint(apiBaseUrl) {
  return joinUrl(apiBaseUrl, 'iam/tenants/info')
}

function loadTenantInfo(endpoint, fetchImpl) {
  if (tenantInfoRequestCache.has(endpoint)) {
    return tenantInfoRequestCache.get(endpoint)
  }

  const request = Promise.resolve()
    .then(() => {
      if (typeof fetchImpl !== 'function') {
        throw new Error('Fetch API is not available')
      }

      return fetchImpl(endpoint, {
        headers: {
          Accept: 'application/json'
        }
      })
    })
    .then(response => {
      if (!response || (typeof response.ok === 'boolean' && !response.ok)) {
        throw new Error(`Tenant info request failed${response?.status ? ` with status ${response.status}` : ''}`)
      }

      return response.json()
    })

  tenantInfoRequestCache.set(endpoint, request)
  return request
}

function normalizeTenantViewerAssets(tenantInfo, tenantNameOverride, fallbackWebsiteUrl) {
  const normalizedTenantNameOverride = resolveString(tenantNameOverride)
  const tenantName = resolveString(normalizedTenantNameOverride, resolveString(tenantInfo?.name, DEFAULT_TENANT_NAME))
  const tenantLogoUrl = getLogoImage(tenantName)
  const tenantCreditsUrl = getCreditsImage(tenantName)
  const viewersConfig = normalizeViewersConfig(tenantInfo?.viewersConfig)

  return {
    tenantName,
    websiteUrl: resolveString(tenantInfo?.websiteUrl, fallbackWebsiteUrl),
    logoUrl: resolveBackendImage(tenantInfo?.logoUrl, defaultLogoUrl, tenantLogoUrl),
    creditsUrl: resolveBackendImage(tenantInfo?.creditsUrl, defaultCreditsUrl, tenantCreditsUrl),
    hideCredits: viewersConfig.hideLogoAndCredits === true,
    defaultAsset: getDefaultAsset(tenantName, normalizedTenantNameOverride ? {} : viewersConfig),
    viewersConfig
  }
}

export const getTenantViewerAssets = ({
  tenantName,
  apiBaseUrl = API_BASE_URL,
  fallbackWebsiteUrl = DEFAULT_WEBSITE_URL,
  fetchImpl = typeof globalThis !== 'undefined' ? globalThis.fetch : undefined
} = {}) => {
  const endpoint = getTenantInfoEndpoint(apiBaseUrl)

  return loadTenantInfo(endpoint, fetchImpl)
    .then(tenantInfo => normalizeTenantViewerAssets(tenantInfo, tenantName, fallbackWebsiteUrl))
    .catch(error => {
      console.warn(`Tenant Assets Service: Failed to load ${endpoint}; using fallback assets.`, error)
      return normalizeTenantViewerAssets(null, tenantName, fallbackWebsiteUrl)
    })
}

function applyAttributes(element, attributes) {
  if (!attributes || typeof attributes !== 'object') return

  Object.entries(attributes).forEach(([name, value]) => {
    if (value !== null && typeof value !== 'undefined' && value !== false) {
      element.setAttribute(name, value === true ? '' : `${value}`)
    }
  })
}

function isAsset(value) {
  return value === 'logo' || value === 'credits'
}

export const createViewerCreditsSlot = ({
  target,
  assets,
  asset,
  visible = true,
  interactive = true,
  attributes = {}
} = {}) => {
  if (!target || typeof target.appendChild !== 'function') {
    throw new TypeError('Tenant Assets Service: A valid credits slot target is required.')
  }

  const documentRef = target.ownerDocument || document
  const rootAttributes = attributes.root || {}
  const imageAttributes = attributes.image || {}
  const existingId = rootAttributes.id

  if (existingId) {
    documentRef.getElementById(existingId)?.remove()
  }

  const root = documentRef.createElement(interactive ? 'a' : 'div')
  const image = documentRef.createElement('img')
  applyAttributes(root, rootAttributes)
  applyAttributes(image, imageAttributes)

  root.classList.add('viewer-credits-slot')
  image.classList.add('viewer-credits-slot__image')

  if (interactive) {
    root.target = '_blank'
    root.rel = 'noreferrer noopener'
  }

  root.appendChild(image)
  target.appendChild(root)

  let destroyed = false
  let runtimeVisible = Boolean(visible)
  let resolvedAssets = null
  let selectedAsset = isAsset(asset) ? asset : null

  const updateVisibility = () => {
    root.hidden = !runtimeVisible || resolvedAssets?.hideCredits === true
  }

  const updateAsset = () => {
    if (!resolvedAssets) return

    const currentAsset = selectedAsset || resolvedAssets.defaultAsset
    const showingCredits = currentAsset === 'credits'
    image.src = showingCredits ? resolvedAssets.creditsUrl : resolvedAssets.logoUrl
    image.alt = showingCredits ? 'Project credits' : 'Project logo'
    root.dataset.viewerCreditsAsset = currentAsset

    if (interactive) {
      if (resolvedAssets.websiteUrl) {
        root.href = resolvedAssets.websiteUrl
      } else {
        root.removeAttribute('href')
      }
      root.setAttribute('aria-label', showingCredits ? 'Open project credits' : 'Open project website')
    }
  }

  updateVisibility()

  const assetsPromise = assets ? Promise.resolve(assets) : getTenantViewerAssets()
  const ready = assetsPromise
    .catch(error => {
      console.warn('Tenant Assets Service: Failed to resolve credits slot assets; using fallback assets.', error)
      return getTenantViewerAssets()
    })
    .then(info => {
      resolvedAssets = info
      if (!destroyed) {
        updateAsset()
        updateVisibility()
      }
      return info
    })

  return {
    element: root,
    ready,
    setVisible(nextVisible) {
      runtimeVisible = Boolean(nextVisible)
      if (!destroyed) updateVisibility()
    },
    setAsset(nextAsset) {
      if (!isAsset(nextAsset)) {
        throw new TypeError(`Tenant Assets Service: Unsupported credits slot asset "${nextAsset}".`)
      }
      selectedAsset = nextAsset
      if (!destroyed) updateAsset()
    },
    destroy() {
      if (destroyed) return
      destroyed = true
      root.remove()
    }
  }
}

export const TenantAssetsService = {
  getLogoImage,
  getLogoMobile,
  getCreditsImage,
  getTenantViewerAssets,
  createViewerCreditsSlot
}

export default TenantAssetsService
